// §4.5 identity-linking widget (Module 17, slice P3-B part 2) — the
// "Messaging" block the customer 360's contact cards embed, one per contact.
//
// Per provider: linked status (masked address — masking IS §7's stated form
// for addresses, unlike the money-field omissions; the server masks unless
// the caller holds `messaging.address.read`, and this widget renders exactly
// what it is given) / "Generate link code" → the one-time modal: the 6-char
// plaintext (shown ONCE — §2.3 stores only the SHA-256), the t.me deep link,
// a QR of the deep link, copy, and the live 15-minute countdown; Revoke with
// a reason. The WhatsApp row renders PARKED while D-232 stands — no verified
// WA identity can exist, so a generate control would mint a code nobody can
// consume.
//
// THE OWN-PROFILE VARIANT (§4.5's second surface) IS DEFERRED, stated: the
// client has no self-id signal (`/v1/auth/tenants` carries memberships, not a
// subject), so a widget that must name `owner_id` for the caller cannot exist
// until an identity feed carries it. Recorded in messaging-layout.tsx and the
// slice report; this component already takes `ownerKind`/`ownerId` so that
// surface mounts it unchanged when the feed lands.
//
// QR: the pure dependency-free encoder is the admin area's `lib/qr.ts` —
// imported as a PURE HELPER (the sales-area precedent of importing
// inventory's `formatMinor`), while the SVG rendering is re-owned here with
// `msg-qr*` classes so module 17's pixels are not styled by module 15's
// stylesheet. Deep links (`https://t.me/{bot}?start=link_{code}`) sit far
// under the encoder's 213-byte ceiling; the fallback still renders the code
// and the link, so an unencodable value degrades to the working pair.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { StatusChip } from "../../../../components/status-chip";
import { encodeQr } from "../../../admin/lib/qr";
import { formatTimestamp } from "../../../../lib/datetime";
import { displayAddress } from "../lib/format";
import { MutationFailureNote } from "../mutation-failure-note";
import { useMessagingPermissions } from "../hooks/permissions";
import {
  useIdentities,
  useIssueLinkCode,
  useRevokeIdentity,
  type ChannelIdentity,
  type LinkCodeIssue,
} from "../hooks/messaging-queries";

const QR_QUIET_ZONE = 4;

/** The deep-link QR, drawn in the browser as inline SVG (no canvas, no
 * network — the admin enrolment rule, for the same reason: the code leaves
 * this machine only by being scanned). */
function LinkQr({ value, title }: { value: string; title: string }) {
  const matrix = useMemo(() => {
    try {
      return encodeQr(value);
    } catch {
      return undefined;
    }
  }, [value]);
  const { t } = useTranslation();

  if (matrix === undefined) {
    return <p className="msg-hint">{t("messaging.link.qrUnavailable")}</p>;
  }
  const span = matrix.size + QR_QUIET_ZONE * 2;
  let d = "";
  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (matrix.dark[row * matrix.size + col] === true) {
        d += `M${String(col + QR_QUIET_ZONE)} ${String(row + QR_QUIET_ZONE)}h1v1h-1z`;
      }
    }
  }
  return (
    <svg
      className="msg-qr"
      viewBox={`0 0 ${String(span)} ${String(span)}`}
      width={176}
      height={176}
      role="img"
      aria-label={title}
      shapeRendering="crispEdges"
      data-testid="msg-linkcode-qr"
    >
      <rect width={span} height={span} className="msg-qr-bg" />
      <path d={d} className="msg-qr-fg" />
    </svg>
  );
}

/** mm:ss until `expiresAt`, ticking; null once past. */
function useCountdown(expiresAt: string | undefined): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (expiresAt === undefined) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [expiresAt]);
  if (expiresAt === undefined) return null;
  const left = Math.floor((new Date(expiresAt).getTime() - now) / 1000);
  if (Number.isNaN(left) || left <= 0) return null;
  const m = Math.floor(left / 60);
  const s = left % 60;
  return `${String(m)}:${s < 10 ? "0" : ""}${String(s)}`;
}

export function LinkWidget({
  ownerKind,
  ownerId,
}: {
  ownerKind: "customer_contact" | "user";
  ownerId: string;
}) {
  const { t } = useTranslation();
  const perms = useMessagingPermissions();
  // The list read rides `messaging.identity.manage` (§5) — held by
  // sales/fa/ga, exactly the §4.5 customer-contact audience. Firing it from a
  // role that lacks the code would be the dead call D-088 forbids, so the
  // whole block simply does not render for them (redaction as absence).
  const canManage = perms.has("messaging.identity.manage");
  const identities = useIdentities(
    { owner_kind: ownerKind, owner_id: ownerId },
    canManage && perms.isResolved,
  );

  const issue = useIssueLinkCode();
  const revoke = useRevokeIdentity();
  const [issued, setIssued] = useState<LinkCodeIssue | undefined>(undefined);
  const [revoking, setRevoking] = useState<ChannelIdentity | undefined>(undefined);

  if (!canManage) return null;

  const rows = identities.data ?? [];
  const telegram = rows.find((row) => row.provider === "telegram" && row.status !== "revoked");

  return (
    <div
      className="msg-link-block"
      data-testid="c360-messaging-block"
      aria-label={t("messaging.link.aria")}
    >
      <h4 className="msg-fact">{t("messaging.link.title")}</h4>

      {identities.isError ? (
        <p className="msg-hint" role="alert" data-testid="msg-link-error">
          {t("messaging.link.error")}
        </p>
      ) : null}

      {/* Telegram row — the live provider. */}
      <div className="msg-link-row" data-testid="msg-link-telegram">
        <span className="msg-provider">{t("messaging.provider.telegram")}</span>
        {telegram === undefined ? (
          <>
            <span className="msg-hint">{t("messaging.link.notLinked")}</span>
            <Button
              size="sm"
              data-testid="msg-linkcode-generate"
              busy={issue.isPending}
              busyLabel={t("messaging.link.generating")}
              onClick={() => {
                issue.reset();
                issue.mutate(
                  { ownerKind, ownerId, provider: "telegram" },
                  {
                    onSuccess: (code) => {
                      setIssued(code);
                    },
                  },
                );
              }}
            >
              {t("messaging.link.generate")}
            </Button>
          </>
        ) : (
          <>
            {/* ONE status element with the §2.2 state as an attribute — the
             * walkthrough contract reads data-status off it (M17-S02). */}
            <StatusChip
              variant={telegram.status === "verified" ? "ok" : "warn"}
              data-testid="msg-identity-status"
              data-status={telegram.status}
            >
              {telegram.status === "verified"
                ? t("messaging.link.linkedChip")
                : t("messaging.link.pendingChip")}
            </StatusChip>
            {/* §4.5 renders the MASKED form for every role (displayAddress
             * mirrors the server's one mask): the full value stays on the
             * wire for messaging.address.read holders, never painted onto
             * the shared 360. */}
            <code className="msg-fact" data-testid="msg-identity-address">
              {displayAddress("telegram", telegram.address)}
            </code>
            {telegram.verified_at !== null ? (
              <span className="msg-hint num">
                {t("messaging.link.verifiedAt", {
                  date: formatTimestamp(telegram.verified_at),
                })}
              </span>
            ) : null}
            {/* ONE element carries both §2.2 flags as attributes — the
             * walkthrough contract reads data-marketing/data-hard off it
             * (M17-S13's §4.5 leg); the label states the stronger fact. */}
            {telegram.opted_out || telegram.opted_out_hard ? (
              <StatusChip
                variant={telegram.opted_out_hard ? "bad" : "warn"}
                dot={false}
                data-testid="msg-identity-optout"
                data-marketing={String(telegram.opted_out)}
                data-hard={String(telegram.opted_out_hard)}
              >
                {telegram.opted_out_hard
                  ? t("messaging.link.optedOutHard")
                  : t("messaging.link.optedOut")}
              </StatusChip>
            ) : null}
            {telegram.status === "pending" ? (
              <Button
                size="sm"
                data-testid="msg-linkcode-generate"
                busy={issue.isPending}
                busyLabel={t("messaging.link.generating")}
                onClick={() => {
                  issue.reset();
                  issue.mutate(
                    { ownerKind, ownerId, provider: "telegram" },
                    {
                      onSuccess: (code) => {
                        setIssued(code);
                      },
                    },
                  );
                }}
              >
                {t("messaging.link.generate")}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="danger"
              data-testid="msg-link-revoke"
              onClick={() => {
                revoke.reset();
                setRevoking(telegram);
              }}
            >
              {t("messaging.link.revoke")}
            </Button>
          </>
        )}
      </div>

      {/* WhatsApp row — parked while D-232 stands (stated, no dead control). */}
      <div className="msg-link-row" data-testid="msg-link-whatsapp">
        <span className="msg-provider">{t("messaging.provider.whatsapp")}</span>
        <span className="msg-hint">{t("messaging.link.waParked")}</span>
      </div>

      {issue.isError ? (
        <MutationFailureNote
          data-testid="msg-mutation-failed"
          text={t("messaging.link.generateFailed")}
          error={issue.error}
        />
      ) : null}
      {revoke.isError ? (
        <MutationFailureNote
          data-testid="msg-mutation-failed"
          text={t("messaging.link.revokeFailed")}
          error={revoke.error}
        />
      ) : null}

      {issued !== undefined ? (
        <CodeModal
          issued={issued}
          onClose={() => {
            setIssued(undefined);
          }}
        />
      ) : null}

      {revoking !== undefined ? (
        <RevokeModal
          identity={revoking}
          busy={revoke.isPending}
          onClose={() => {
            setRevoking(undefined);
          }}
          onConfirm={(reason) => {
            revoke.mutate(
              { id: revoking.id, reason },
              {
                onSuccess: () => {
                  setRevoking(undefined);
                },
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}

/** The one-time code modal (§3.2.1). Closing it loses the plaintext forever —
 * the warning sentence says so before the close, not after (D-069). */
function CodeModal({ issued, onClose }: { issued: LinkCodeIssue; onClose: () => void }) {
  const { t } = useTranslation();
  const countdown = useCountdown(issued.expires_at);
  const [copied, setCopied] = useState(false);

  return (
    <Modal
      label={t("messaging.link.modalTitle")}
      heading={t("messaging.link.modalTitle")}
      closeLabel={t("messaging.common.close")}
      onClose={onClose}
      className="msg-modal"
      testId="msg-linkcode-modal"
    >
      <div className="msg-link-block">
        <p className="msg-hint">{t("messaging.link.codeOnce")}</p>
        <div className="msg-link-row">
          <code
            className="msg-code"
            data-testid="msg-linkcode-code"
            aria-label={t("messaging.link.codeAria")}
          >
            {issued.code}
          </code>
          <Button
            size="sm"
            data-testid="msg-link-copy"
            onClick={() => {
              // Clipboard is absent on insecure contexts (the admin recovery
              // screen guards the same way); the code stays selectable text.
              const { clipboard } = navigator as unknown as { clipboard?: Clipboard };
              if (clipboard !== undefined) {
                void clipboard.writeText(issued.code).then(() => {
                  setCopied(true);
                });
              }
            }}
          >
            {copied ? t("messaging.common.copied") : t("messaging.link.copyCode")}
          </Button>
        </div>
        {countdown !== null ? (
          <p className="msg-hint num" data-testid="msg-linkcode-countdown">
            {t("messaging.link.expiresIn", { time: countdown })}
          </p>
        ) : (
          <p className="msg-hint" role="status" data-testid="msg-link-expired">
            {t("messaging.link.expired")}
          </p>
        )}
        {issued.deep_link !== null ? (
          <>
            {/* The URL ITSELF is the visible text — the customer may need to
             * type it, and the walkthrough contract asserts the t.me address
             * on this element; the dictionary label rides as the aria-label. */}
            <a
              href={issued.deep_link}
              target="_blank"
              rel="noreferrer"
              aria-label={t("messaging.link.deepLink")}
              data-testid="msg-linkcode-deeplink"
            >
              {issued.deep_link}
            </a>
            <LinkQr value={issued.deep_link} title={t("messaging.link.qrAria")} />
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/** Revoke with a stated consequence and a required reason (§3.2's audit needs
 * one; the schema demands 1–400 chars). */
function RevokeModal({
  identity,
  busy,
  onClose,
  onConfirm,
}: {
  identity: ChannelIdentity;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  return (
    <Modal
      label={t("messaging.link.revokeTitle")}
      heading={t("messaging.link.revokeTitle")}
      closeLabel={t("messaging.common.close")}
      onClose={onClose}
      className="msg-modal"
      testId="msg-revoke-modal"
    >
      <div className="msg-link-block">
        <p className="msg-hint">{t("messaging.link.revokeBody")}</p>
        <p className="msg-fact">
          <code>{displayAddress(identity.provider, identity.address)}</code>
        </p>
        <Field label={t("messaging.link.revokeReason")} wide>
          <input
            value={reason}
            data-testid="msg-revoke-reason"
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </Field>
        <div className="msg-toolbar">
          <Button data-testid="msg-revoke-cancel" onClick={onClose}>
            {t("messaging.common.cancel")}
          </Button>
          <Button
            variant="danger"
            data-testid="msg-revoke-confirm"
            disabled={reason.trim().length === 0}
            busy={busy}
            busyLabel={t("messaging.common.saving")}
            onClick={() => {
              onConfirm(reason.trim());
            }}
          >
            {t("messaging.link.revokeConfirm")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
